/**
 * BmrReview — AI-308
 *
 * Single-screen BMR review accelerator. Route: /quality/bmr-review/$id.
 *
 * Layout (drafts/55 §6.3):
 *   - Header  — batch code, product, site, line, status badge, "Run review" CTA.
 *   - Left    — AI findings sorted CRITICAL → INFO, each with citations
 *               + accept / dismiss / convert-to-DEV actions.
 *   - Right   — field grid, grouped by section, flagged rows highlighted.
 *   - Footer  — engine summary + last-run invocation IDs (for InspectorMode).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { buildHeaders, apiUrl } from '../../api/client';
import { useParams } from '@tanstack/react-router';
import { Button } from '@edms/design-system';
import styles from './BmrReview.module.css';

interface BmrExecution {
  bmr_execution_id: string;
  tenant_id: string;
  batch_code: string;
  product_code: string;
  site_code: string;
  line_code: string | null;
  status: 'EXECUTING' | 'UNDER_REVIEW' | 'RELEASED' | 'REJECTED';
  started_at_utc: string;
  completed_at_utc: string | null;
  released_at_utc: string | null;
  reviewer_user_id: string | null;
  findingsCount?: number;
}

interface BmrField {
  field_id: string;
  template_field_key: string;
  section: string | null;
  step_number: string | null;
  label: string;
  field_kind: 'NUMERIC' | 'TEXT' | 'SIGNATURE' | 'SELECT' | 'TIMESTAMP' | 'BOOLEAN' | 'NOTE';
  value_text: string | null;
  value_numeric: number | null;
  value_unit: string | null;
  spec_low: number | null;
  spec_high: number | null;
  spec_target: number | null;
  recorded_at_utc: string | null;
  recorded_by_user_id: string | null;
  signed_at_utc: string | null;
  signed_by_user_id: string | null;
  requires_second_sign: boolean;
  flagged_for_review: boolean;
}

interface BmrFinding {
  finding_id: string;
  bmr_execution_id: string;
  ai_run_id: string | null;
  severity: 'CRITICAL' | 'MAJOR' | 'MINOR' | 'INFO';
  category: string;
  engine: 'hard' | 'spec' | 'soft' | 'pattern';
  cited_field_ids: string[];
  ai_message: string;
  confidence: number | null;
  reviewer_verdict: 'ACCEPTED' | 'DISMISSED' | 'CONVERTED_TO_DEV' | null;
  reviewer_user_id: string | null;
  reviewer_recorded_at: string | null;
  created_at_utc: string;
}

interface BmrDetailResponse {
  execution: BmrExecution;
  fields: BmrField[];
  findings: BmrFinding[];
}

const SEVERITY_ORDER: Record<BmrFinding['severity'], number> = {
  CRITICAL: 0, MAJOR: 1, MINOR: 2, INFO: 3,
};

export function BmrReview() {
  const params = useParams({ strict: false }) as { id?: string };
  const execId = params.id || '';

  const [detail, setDetail] = useState<BmrDetailResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!execId) return;
    setLoading(true);
    setError(null);
    try {
      const r = await fetch(apiUrl(`/api/v1/bmr/executions/${encodeURIComponent(execId)}`), { headers: buildHeaders() });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d: BmrDetailResponse = await r.json();
      setDetail(d);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [execId]);

  useEffect(() => { void refresh(); }, [refresh]);

  const runReview = async () => {
    if (!execId) return;
    setRunning(true);
    setError(null);
    try {
      const r = await fetch(apiUrl(`/api/v1/ai/bmr/${encodeURIComponent(execId)}/run-review`), { method: 'POST', headers: buildHeaders() });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      await r.json();
      await refresh();
    } catch (err) {
      setError(`Review failed: ${(err as Error).message}`);
    } finally {
      setRunning(false);
    }
  };

  const setVerdict = async (findingId: string, verdict: BmrFinding['reviewer_verdict']) => {
    if (!verdict) return;
    try {
      const r = await fetch(apiUrl(`/api/v1/bmr/findings/${encodeURIComponent(findingId)}/verdict`), {
        method: 'POST',
        headers: buildHeaders({ json: true }),
        body: JSON.stringify({ verdict, userId: 'reviewer' }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      await refresh();
    } catch (err) {
      setError(`Verdict failed: ${(err as Error).message}`);
    }
  };

  // Sort findings critical-first, then by created.
  const sortedFindings = useMemo(() => {
    return (detail?.findings || []).slice().sort((a, b) => {
      const sd = SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity];
      if (sd !== 0) return sd;
      return a.created_at_utc.localeCompare(b.created_at_utc);
    });
  }, [detail?.findings]);

  const fieldsBySection = useMemo(() => {
    const map = new Map<string, BmrField[]>();
    for (const f of detail?.fields || []) {
      const sec = f.section || '—';
      if (!map.has(sec)) map.set(sec, []);
      map.get(sec)!.push(f);
    }
    return map;
  }, [detail?.fields]);

  const fieldIndex = useMemo(() => {
    const map = new Map<string, BmrField>();
    for (const f of detail?.fields || []) map.set(f.field_id, f);
    return map;
  }, [detail?.fields]);

  const findingSummary = useMemo(() => {
    const acc = { CRITICAL: 0, MAJOR: 0, MINOR: 0, INFO: 0, total: 0 };
    for (const f of detail?.findings || []) {
      acc[f.severity]++;
      acc.total++;
    }
    return acc;
  }, [detail?.findings]);

  if (!execId) {
    return <div className={styles.empty}>No BMR execution ID provided.</div>;
  }

  return (
    <div className={styles.screen}>
      <header className={styles.header}>
        <div className={styles.headLeft}>
          <h1 className={styles.title}>
            BMR review <span className={styles.batchCode}>{detail?.execution.batch_code || '…'}</span>
          </h1>
          <p className={styles.subtitle}>
            {detail
              ? `Product ${detail.execution.product_code} · ${detail.execution.site_code} · line ${detail.execution.line_code || '—'} · started ${new Date(detail.execution.started_at_utc).toLocaleString()}`
              : 'Loading execution detail…'}
          </p>
        </div>
        <div className={styles.headRight}>
          {detail && (
            <span className={`${styles.statusBadge} ${styles[`status_${detail.execution.status}`]}`}>
              {detail.execution.status}
            </span>
          )}
          <Button variant="primary" onClick={runReview} disabled={running || !detail}>
            {running ? 'Running review…' : '✦ Run AI review'}
          </Button>
        </div>
      </header>

      {error && <div className={styles.error} role="alert">{error}</div>}

      <div className={styles.summaryBar} aria-label="Findings summary">
        <span className={`${styles.summaryPill} ${styles.sevCRITICAL}`}>CRITICAL · {findingSummary.CRITICAL}</span>
        <span className={`${styles.summaryPill} ${styles.sevMAJOR}`}>MAJOR · {findingSummary.MAJOR}</span>
        <span className={`${styles.summaryPill} ${styles.sevMINOR}`}>MINOR · {findingSummary.MINOR}</span>
        <span className={`${styles.summaryPill} ${styles.sevINFO}`}>INFO · {findingSummary.INFO}</span>
        <span className={styles.summaryTotal}>{findingSummary.total} total</span>
      </div>

      <div className={styles.body}>
        {/* AI findings panel */}
        <section className={styles.findingsPanel} aria-label="AI findings">
          <h2 className={styles.panelTitle}>AI findings</h2>
          {loading && <p className={styles.muted}>Loading…</p>}
          {!loading && sortedFindings.length === 0 && (
            <p className={styles.muted}>
              {detail ? 'No findings yet. Click ✦ Run AI review to evaluate this batch.' : '…'}
            </p>
          )}
          <ul className={styles.findingList}>
            {sortedFindings.map((f) => (
              <li key={f.finding_id} className={`${styles.finding} ${styles[`sevBorder_${f.severity}`]}`}>
                <header className={styles.findingHead}>
                  <span className={`${styles.sevTag} ${styles[`sev_${f.severity}`]}`}>{f.severity}</span>
                  <span className={styles.findingCat}>{f.category.replaceAll('_', ' ').toLowerCase()}</span>
                  <span className={styles.findingEngine}>via {f.engine}</span>
                  {f.confidence != null && (
                    <span className={styles.findingConfidence}>{Math.round(f.confidence * 100)}%</span>
                  )}
                </header>
                <p className={styles.findingMsg}>{f.ai_message}</p>
                {f.cited_field_ids.length > 0 && (
                  <ul className={styles.citationList}>
                    {f.cited_field_ids.map((id) => {
                      const fld = fieldIndex.get(id);
                      return (
                        <li key={id} className={styles.citationChip}>
                          {fld ? `${fld.section || ''} / ${fld.label}` : id.slice(0, 8) + '…'}
                        </li>
                      );
                    })}
                  </ul>
                )}
                {f.reviewer_verdict ? (
                  <p className={styles.verdictDone}>
                    Reviewer verdict: <strong>{f.reviewer_verdict.replaceAll('_', ' ')}</strong>
                    {f.reviewer_recorded_at && ` · ${new Date(f.reviewer_recorded_at).toLocaleString()}`}
                  </p>
                ) : (
                  <div className={styles.verdictRow}>
                    <button type="button" className={styles.btnAccept}    onClick={() => setVerdict(f.finding_id, 'ACCEPTED')}>Accept</button>
                    <button type="button" className={styles.btnDismiss}   onClick={() => setVerdict(f.finding_id, 'DISMISSED')}>Dismiss</button>
                    <button type="button" className={styles.btnConvert}   onClick={() => setVerdict(f.finding_id, 'CONVERTED_TO_DEV')}>Convert to DEV</button>
                  </div>
                )}
              </li>
            ))}
          </ul>
          <p className={styles.disclaimer}>
            AI advisory only — verify against source records before relying on it. AI never signs or approves.
          </p>
        </section>

        {/* Field grid panel */}
        <section className={styles.fieldsPanel} aria-label="BMR fields">
          <h2 className={styles.panelTitle}>Batch fields</h2>
          {Array.from(fieldsBySection.entries()).map(([section, fs]) => (
            <div key={section} className={styles.sectionBlock}>
              <h3 className={styles.sectionHeader}>{section}</h3>
              <table className={styles.fieldTable}>
                <thead>
                  <tr>
                    <th>Step</th>
                    <th>Label</th>
                    <th>Value</th>
                    <th>Spec</th>
                    <th>Signed</th>
                  </tr>
                </thead>
                <tbody>
                  {fs.map((f) => {
                    const flagged = f.flagged_for_review;
                    const oos = f.field_kind === 'NUMERIC' && f.value_numeric != null && (
                      (f.spec_low != null && f.value_numeric < f.spec_low) ||
                      (f.spec_high != null && f.value_numeric > f.spec_high)
                    );
                    return (
                      <tr key={f.field_id} className={oos ? styles.rowOos : flagged ? styles.rowFlagged : ''}>
                        <td className={styles.mono}>{f.step_number || '—'}</td>
                        <td>{f.label}</td>
                        <td>
                          {f.field_kind === 'NUMERIC'
                            ? (f.value_numeric != null ? `${f.value_numeric} ${f.value_unit || ''}` : '—')
                            : (f.value_text || (f.signed_at_utc ? '✓ signed' : '—'))}
                        </td>
                        <td className={styles.muted}>
                          {f.spec_low != null || f.spec_high != null
                            ? `${f.spec_low ?? '-∞'} … ${f.spec_high ?? '+∞'} ${f.value_unit || ''}`
                            : '—'}
                        </td>
                        <td className={styles.muted}>
                          {f.field_kind === 'SIGNATURE' || f.requires_second_sign
                            ? (f.signed_at_utc ? `✓` : <span className={styles.missing}>missing</span>)
                            : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ))}
        </section>
      </div>
    </div>
  );
}
